import React, { useCallback } from 'react';
import { Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Add as AddIcon, Close as DeleteIcon } from '@mui/icons-material';
import { partial, times } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { useAudio } from '@dyl/systems/AudiosManager';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import AudioPicker from '@dyl/components/AudioPicker';

const useStyles = makeStyles(theme => ({
    addButtonContainer: {
        maxWidth: theme.breakpoints.values.md,
    },
    addButton: {
        marginBottom: theme.spacing(2),

        [theme.breakpoints.up('sm')]: {
            marginLeft: `${Math.round(100 * 1 / 12)}%`,
        },
    },
}));

export default function BackupAudiosSection({ index }) {
    const classes = useStyles();

    const [{ length }, setBackups] = useTaskField('items', index, 'backups');

    const addNew = useCallback(() => {
        setBackups(prev => {
            const next = [...prev];

            if (next.length < 3) {
                next.push({
                    audio_id: null,
                    delay: 6000,
                });
            }

            return next;
        });
    }, [setBackups]);

    return (
        <>
            <Typography variant='subtitle1'>
                Backup Audios
            </Typography>

            {times(length, answerIndex => (
                <Item
                    key={answerIndex}
                    index={answerIndex}
                    itemIndex={index}
                    allowRemove={length > 1}
                />
            ))}

            {length < 3 && (
                <div className={classes.addButtonContainer}>
                    <Button
                        onClick={addNew}
                        className={classes.addButton}
                        color='primary'
                    >
                        <AddIcon />&nbsp;Add
                    </Button>
                </div>
            )}
        </>
    );
}

/** @param {string} serialized */
function serializeDelay(serialized) {
    const seconds = Number.parseFloat(serialized);

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
}

const useItemStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
        marginBottom: theme.spacing(2),
    },
    removeButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

function Item({ itemIndex, index, allowRemove = false }) {
    const classes = useItemStyles();
    const useField = partial(useTaskField, 'items', itemIndex, 'backups', index);

    const [, setBackups] = useTaskField('items', itemIndex, 'backups');
    const remove = useCallback(() => {
        setBackups(prev => prev.filter((b, i) => i !== index));
    }, [setBackups, index]);

    const [delay, setDelay, delayError] = useField('delay');
    const onDelayChange = useChangeEvent(setDelay, { transform: serializeDelay });

    const [audioId, setAudioId, audioError] = useField('audio_id');

    const { transcript } = useAudio(audioId) ?? {};

    return (
        <Grid container spacing={1} className={classes.root}>
            <Grid item xs={2} sm={1} className={classes.removeButtonContainer}>
                <IconButton
                    onClick={remove}
                    disabled={!allowRemove}
                    style={{ marginBottom: 20 }}
                    size="large">
                    <DeleteIcon />
                </IconButton>
            </Grid>
            <Grid item xs={10} sm={3}>
                <TextField
                    id={`task-backup-delay-${index}`}
                    label='Delay'
                    value={(delay ?? 6000) / 1000}
                    onChange={onDelayChange}
                    helperText={delayError || 'In seconds'}
                    error={!!delayError}
                    type='number'
                    variant='filled'
                    fullWidth
                />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
                <AudioPicker
                    id={`task-backup-audio-${index}`}
                    label={`Backup Audio ${index + 1}`}
                    value={audioId ?? ''}
                    onChange={id => setAudioId(id)}
                    error={!!audioError}
                    helperText={audioError || 'Must be .mp3'}
                    allowEmpty
                    fullWidth
                />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
                <TextField
                    id={`task-backup-audio-transcript-${index}`}
                    label={`Backup Audio ${index + 1} Transcript`}
                    value={transcript ?? 'No transcript assigned'}
                    disabled
                    variant='filled'
                    fullWidth
                    multiline
                />
            </Grid>
        </Grid>
    );
}